import {Component, ViewChild} from '@angular/core';
import {DeclinaisonsService} from '../../core/services/entities/declinaisons.service';
import {HELP_FOLDERS, MSG_KEY, MSG_SEVERITY} from "../../core/constants";
import {DeclinaisonDTO} from "../../core/dtos/declinaison-dto";
import {Subscription} from "rxjs";
import {GraphQLService} from "../../core/services/technique/graphql.service";
import {UtilsService} from "../../core/utils/utils.service";
import {saveAs} from "file-saver";
import {InternationalizationService} from "../../core/services/i8n/i8n.service";
import {ToastService} from "../../core/services/technique/toast.service";
import {ResponseWrapper} from "../../core/suppliers/wrappers/response-wrapper";
import {IsDeletableObject} from "../../core/models/is-deletable-object";
import {confirm} from "devextreme/ui/dialog";
import {OverlayPanel} from "primeng/overlaypanel";
import {Auth2Service} from "../../core/services/security/auth2.service";

@Component({
  selector: 'yo-declinaisons',
  templateUrl: './declinaisons.component.html',
  styleUrls: ['./declinaisons.component.scss']
})
export class DeclinaisonsComponent {

  declinaisons: DeclinaisonDTO[] = [];

  pathFile: string = HELP_FOLDERS.REFERENTIEL + '/declinaisons/declinaisons';

  selectedRows: number[] = [];

  subGraphQL: Subscription;

  subXls: Subscription;

  subImport: Subscription;

  subDeclinaisonSaved: Subscription;

  errors: any[] = [];

  displayDeletePopupErrors: boolean = false;

  displayImportPopupErrors: boolean = false;

  @ViewChild('opImportXls') opImportXls: OverlayPanel;

  constructor(public declinaisonsService: DeclinaisonsService,
              private readonly graphqlSvc: GraphQLService,
              private utilsSvc: UtilsService,
              private readonly i8nSvc: InternationalizationService,
              private readonly toastSvc: ToastService,
              private readonly auth2Svc: Auth2Service) { }

  ngOnInit() {
    this.fetchData();
    this.subscribeDeclinaisonSaved();
  }

  ngOnDestroy() {
    this.utilsSvc.unsubscribe(this.subGraphQL);
    this.utilsSvc.unsubscribe(this.subXls);
    this.utilsSvc.unsubscribe(this.subImport);
    this.utilsSvc.unsubscribe(this.subDeclinaisonSaved);
  }

  fetchData = (): void => {
    const idsSites: number[] = this.auth2Svc.utilisateur.sites.map(s => s.id);
    this.subGraphQL = this.graphqlSvc.sendQuery(`
      {
          allDeclinaisons(filters: {
          siteIds: [${idsSites}]
        }) {
              id,
              libelle,
              site { id, libelle },
              code,
              typeProduit { id, libelle },
              actif
          }
      }
    `)
      .subscribe((res) => {
        this.declinaisons = res.allDeclinaisons;
      });
  }

  openDialogEdition = (declinaison?: DeclinaisonDTO): void => {
    this.declinaisonsService.announceOpeningEditionDialog(declinaison);
  }

  subscribeDeclinaisonSaved = (): void => {
    this.subDeclinaisonSaved = this.declinaisonsService.declinaisonSaved$
      .subscribe(() => this.fetchData());
  }

  displayImportErrors = (): void => { this.displayImportPopupErrors = true; }

  exportXslx = (): void => {
    this.subXls = this.declinaisonsService.export(this.selectedRows)
      .subscribe(response => {
        const filename = `déclinaisons-export-${new Date().getTime()}.xlsx`;
        const blob = new Blob([response], {type: 'application/vnd.ms-excel'});
        this.selectedRows = [];
        saveAs(blob, filename);
      });
  }

  canDelete = (): boolean => this.selectedRows?.length > 0;

  deleteValues = async (): Promise<void> => {
    this.errors = [];
    let idsToDeleteInTable = Object.assign([], this.selectedRows);

    let isDeletionValidated: boolean = await confirm(this.i8nSvc.getLabelFromCode(idsToDeleteInTable.length > 1 ? "CONFIRM_DELETION_PLURAL" : "CONFIRM_DELETION_SINGULAR", null), this.i8nSvc.getLabelFromCode("Suppression", null));

    if (!isDeletionValidated)
      return new Promise(null);

    this.declinaisonsService.delete(this.selectedRows).subscribe(response => {
      const res: any = (response as ResponseWrapper<IsDeletableObject>).one;

      if (res) {
        if (res?.messagesErrorList?.length) {
          for (const error of res.messagesErrorList) {
            const labelsError: any = this.i8nSvc.getLabelFromCode(error.code, null);
            const infosLine: string = labelsError.replace('{}', error.args);
            this.errors.push({infosLine});
            const elementToKeep = this.declinaisons.find(u => u.code === error.args);
            idsToDeleteInTable = idsToDeleteInTable.filter(id => elementToKeep.id !== id);
          }
          this.displayDeleteErrors();
        } else {
          this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, idsToDeleteInTable.length > 1 ? `Les déclinaisons ont bien été supprimées` : `La déclinaison a bien été supprimée`);
        }
        this.declinaisons = this.declinaisons.filter(declinaison => !idsToDeleteInTable.find(id => id === declinaison.id));
      }
    });
  }

  displayDeleteErrors = (): void => {
    this.displayDeletePopupErrors = true;
  }

  closeErrors = (): void => {
    this.errors = [];
    this.displayDeletePopupErrors = false;
    this.displayImportPopupErrors = false;
  }

  importXslx = ($event: any): void => {
    this.errors = [];
    if ($event.target.files && $event.target.files.length) {
      const file: File = $event.target.files[0];
      this.opImportXls.hide();
      this.subImport = this.declinaisonsService.importFromXlsx(file)
        .subscribe((response) => {
          const res: any = response.one;
          if (res.allElementsImported) {
            this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, `Le fichier a été importé avec succès`);
          } else {
            for (const item of Object.entries(res.reports)) {
              let values: any[] = Array.of(item[1]);
              const labelsError: any[] = values.map(val => val.map(v => this.i8nSvc.getLabelFromCode(v.code, v.args.split(','))).join(', '));
              this.errors.push({infosLine: item[0], labelsError});
            }
          }

          for (const item of res.elementsImported) {
            let index = this.declinaisons.findIndex(atelier => atelier.id == item.id);
            if (index >= 0)
              this.declinaisons[index] = item;
            else
              this.declinaisons.push(item)
          }

          if (this.errors?.length)
            this.displayImportErrors();
        });
    }
  }

  calculateSiteValue = (declinaison: DeclinaisonDTO): string => declinaison?.site?.libelle;

}
